import { useState } from 'react';
import { Surface, Badge, Button, Grid, PageHeader, Input, Modal, Checkbox } from '../../ui/components';
import { Plus, Edit } from 'lucide-react';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const mockPrograms = [
  { id: '1', name: 'Conventional', short_name: 'Conventional', min_credit: 620, max_ltv: 97, min_down: 3, is_active: true, applications: 145 },
  { id: '2', name: 'FHA', short_name: 'FHA', min_credit: 580, max_ltv: 96.5, min_down: 3.5, is_active: true, applications: 98 },
  { id: '3', name: 'VA', short_name: 'VA', min_credit: 620, max_ltv: 100, min_down: 0, is_active: true, applications: 67 },
  { id: '4', name: 'USDA', short_name: 'USDA', min_credit: 640, max_ltv: 100, min_down: 0, is_active: true, applications: 23 },
  { id: '5', name: 'Jumbo', short_name: 'Jumbo', min_credit: 700, max_ltv: 80, min_down: 20, is_active: true, applications: 34 },
];

const AdminPrograms = () => {
  const [programs] = useState(mockPrograms);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const handleEdit = (program) => {
    setSelectedProgram(program);
    setEditOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Loan Programs"
        description="Configure available loan products"
        actions={<Button variant="primary" icon={Plus}>Add Program</Button>}
      />

      <Grid cols={4} gap="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-6)' }}>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Programs</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{programs.length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Active Programs</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-success-600)' }}>{programs.filter(p => p.is_active).length}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Total Applications</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700 }}>{programs.reduce((sum, p) => sum + p.applications, 0)}</div>
        </Surface>
        <Surface padding="var(--lk-space-5)">
          <div className="lk-overline" style={{ marginBottom: 4 }}>Most Popular</div>
          <div style={{ fontSize: 'var(--lk-text-2xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>{[...programs].sort((a, b) => b.applications - a.applications)[0]?.short_name}</div>
        </Surface>
      </Grid>

      <Surface padding="0" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="lk-table">
            <thead>
              <tr>
                <th>Program</th>
                <th>Min Credit</th>
                <th>Max LTV</th>
                <th>Min Down</th>
                <th>Applications</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((program) => (
                <tr key={program.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{program.name}</div>
                    <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>{program.short_name}</div>
                  </td>
                  <td style={{ fontWeight: 600 }}>{program.min_credit}</td>
                  <td style={{ fontWeight: 600 }}>{program.max_ltv}%</td>
                  <td style={{ fontWeight: 600 }}>{program.min_down}%</td>
                  <td><Badge variant="primary">{program.applications}</Badge></td>
                  <td>
                    <Badge variant={program.is_active ? 'success' : 'neutral'}>
                      {program.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Button size="sm" variant="ghost" icon={Edit} onClick={() => handleEdit(program)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Program"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button variant="primary">Save Changes</Button>
          </>
        }
      >
        {selectedProgram && (
          <>
            <Input label="Program Name" defaultValue={selectedProgram.name} />
            <Input label="Short Name" defaultValue={selectedProgram.short_name} style={{ marginTop: 'var(--lk-space-3)' }} />
            <Grid cols={2} gap="var(--lk-space-3)" style={{ marginTop: 'var(--lk-space-3)' }}>
              <Input label="Min Credit Score" type="number" defaultValue={selectedProgram.min_credit} />
              <Input label="Max LTV (%)" type="number" defaultValue={selectedProgram.max_ltv} />
            </Grid>
            <Input label="Min Down Payment (%)" type="number" defaultValue={selectedProgram.min_down} style={{ marginTop: 'var(--lk-space-3)' }} />
            <Checkbox label="Active" checked={selectedProgram.is_active} onChange={() => {}} style={{ marginTop: 'var(--lk-space-3)' }} />
          </>
        )}
      </Modal>

      <FloatingOwenAssistant context="admin-programs" />
      <FloatingKnowledge customContent={{
        label: 'Programs',
        title: 'Loan program configuration',
        body: 'Configure and manage loan program offerings.',
        bullets: ['Set program guidelines', 'Configure eligibility rules', 'Manage program availability'],
        resources: [{ label: 'Program guidelines', href: '#' }],
      }} />
    </div>
  );
};

export default AdminPrograms;
